import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, X } from "lucide-react";

import type { PermitApplication, PermitApplicationPayload } from "@/api/permit_applications";
import { listMunicipalityCollaborators } from "@/api/municipality_collaborators";
import {
  addPermitApplicationReviewer,
  removePermitApplicationReviewer,
  updatePermitApplicationReviewer,
} from "@/api/permit_application_reviewers";
import { PERMIT_REVIEWER_DECISION_LABELS } from "@/api/permit_applications";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { invalidatePermitSuitePipeline } from "@/lib/permitSuiteQueryKeys";

type PermitApplicationTeamFieldsProps = {
  municipalityId: number;
  application: PermitApplication;
  onUpdate: (patch: Partial<PermitApplicationPayload>) => void;
  updating: boolean;
};

const UNASSIGNED = "unassigned";
const ADD_REVIEWER = "add_reviewer";
const UNDECIDED = "undecided";

export function PermitApplicationTeamFields({
  municipalityId,
  application,
  onUpdate,
  updating,
}: PermitApplicationTeamFieldsProps) {
  const queryClient = useQueryClient();
  const reviewers = application.reviewers ?? [];
  const reviewerUserIds = new Set(reviewers.map((reviewer) => reviewer.user_id));

  const { data: collaborators = [], isLoading, isError } = useQuery({
    queryKey: ["municipality_collaborators", municipalityId],
    queryFn: () => listMunicipalityCollaborators(municipalityId),
  });

  const invalidate = () => {
    invalidatePermitSuitePipeline(queryClient, municipalityId);
    queryClient.invalidateQueries({
      queryKey: ["permit_application_events", municipalityId, application.id],
    });
  };

  const addReviewerMutation = useMutation({
    mutationFn: (userId: number) =>
      addPermitApplicationReviewer(municipalityId, application.id, userId),
    onSuccess: invalidate,
  });

  const removeReviewerMutation = useMutation({
    mutationFn: (userId: number) =>
      removePermitApplicationReviewer(municipalityId, application.id, userId),
    onSuccess: invalidate,
  });

  const updateReviewerMutation = useMutation({
    mutationFn: ({
      userId,
      patch,
    }: {
      userId: number;
      patch: Parameters<typeof updatePermitApplicationReviewer>[3];
    }) => updatePermitApplicationReviewer(municipalityId, application.id, userId, patch),
    onSuccess: invalidate,
  });

  const reviewerBusy =
    addReviewerMutation.isPending ||
    removeReviewerMutation.isPending ||
    updateReviewerMutation.isPending;
  const availableCollaborators = collaborators.filter(
    (collaborator) => !reviewerUserIds.has(collaborator.id),
  );

  return (
    <div className="space-y-5" data-testid="permit-application-team-fields">
      {isError ? (
        <p className="text-sm text-destructive">Could not load team members for this municipality.</p>
      ) : null}
      <div className="space-y-2">
        <Label htmlFor="permit-detail-owner">Owner</Label>
        <Select
          value={application.assigned_to_id ? String(application.assigned_to_id) : UNASSIGNED}
          onValueChange={(value) =>
            onUpdate({
              assigned_to_id: value === UNASSIGNED ? null : Number(value),
            })
          }
          disabled={updating || isLoading}
        >
          <SelectTrigger id="permit-detail-owner">
            <SelectValue placeholder={isLoading ? "Loading team…" : "Unassigned"} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={UNASSIGNED}>Unassigned</SelectItem>
            {collaborators.map((collaborator) => (
              <SelectItem key={collaborator.id} value={String(collaborator.id)}>
                {collaborator.email}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-3">
        <Label>Reviewers</Label>
        {reviewers.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No reviewers yet. Add colleagues who should review this permit.
          </p>
        ) : (
          <div className="space-y-3">
            {reviewers.map((reviewer) => (
              <div key={reviewer.id} className="space-y-3 rounded-lg border bg-background p-3">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="text-sm font-medium">{reviewer.email}</p>
                    {reviewer.decided_at ? (
                      <p className="text-xs text-muted-foreground">
                        Updated {new Date(reviewer.decided_at).toLocaleString()}
                      </p>
                    ) : null}
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 shrink-0"
                    disabled={reviewerBusy || updating}
                    aria-label={`Remove ${reviewer.email}`}
                    onClick={() => removeReviewerMutation.mutate(reviewer.user_id)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-1">
                    <Label className="text-xs">Role (optional)</Label>
                    <Input
                      key={`role-${reviewer.id}-${reviewer.role_label ?? ""}`}
                      defaultValue={reviewer.role_label ?? ""}
                      placeholder="e.g. Zoning, Fire"
                      disabled={reviewerBusy || updating}
                      onBlur={(event) => {
                        const value = event.target.value.trim() || null;
                        if (value === (reviewer.role_label ?? null)) return;
                        updateReviewerMutation.mutate({
                          userId: reviewer.user_id,
                          patch: { role_label: value },
                        });
                      }}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Decision</Label>
                    <Select
                      value={reviewer.decision ?? UNDECIDED}
                      onValueChange={(value) =>
                        updateReviewerMutation.mutate({
                          userId: reviewer.user_id,
                          patch: {
                            decision: value === UNDECIDED ? null : (value as "approved" | "rejected" | "needs_revision"),
                          },
                        })
                      }
                      disabled={reviewerBusy || updating}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={UNDECIDED}>Undecided</SelectItem>
                        {Object.entries(PERMIT_REVIEWER_DECISION_LABELS).map(([key, label]) => (
                          <SelectItem key={key} value={key}>
                            {label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Notes</Label>
                  <Textarea
                    defaultValue={reviewer.notes ?? ""}
                    placeholder="Reviewer notes"
                    rows={2}
                    disabled={reviewerBusy || updating}
                    onBlur={(event) => {
                      const value = event.target.value.trim() || null;
                      if (value === (reviewer.notes ?? null)) return;
                      updateReviewerMutation.mutate({
                        userId: reviewer.user_id,
                        patch: { notes: value },
                      });
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        )}

        <Select
          value={ADD_REVIEWER}
          onValueChange={(value) => {
            if (value === ADD_REVIEWER) return;
            addReviewerMutation.mutate(Number(value));
          }}
          disabled={reviewerBusy || updating || isLoading || availableCollaborators.length === 0}
        >
          <SelectTrigger>
            <SelectValue
              placeholder={
                isLoading
                  ? "Loading team…"
                  : availableCollaborators.length === 0
                    ? "All collaborators added"
                    : "Add reviewer"
              }
            />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ADD_REVIEWER} disabled>
              Add reviewer
            </SelectItem>
            {availableCollaborators.map((collaborator) => (
              <SelectItem key={collaborator.id} value={String(collaborator.id)}>
                {collaborator.email}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {addReviewerMutation.isPending ? (
          <p className="flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="h-3 w-3 animate-spin" />
            Adding reviewer…
          </p>
        ) : null}
      </div>
    </div>
  );
}
